import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ListaUsuarios from "../../componentesEspeciales/usuariosAdmin/listaUsuarios";
import FiltrosUsuarios from "../../componentesEspeciales/usuariosAdmin/filtrosUsuarios";

function Usuarios() {
    const navigate = useNavigate();

    // Buscador
    const [query, setQuery]           = useState("");
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando]     = useState(false);
    const [buscado, setBuscado]       = useState(false);
    const timerRef                    = useRef(null);

    // Lista paginada
    const [lista, setLista]           = useState([]);
    const [cargandoLista, setCargandoLista] = useState(false);
    const [pagina, setPagina]         = useState(1);
    const [paginas, setPaginas]       = useState(1);
    const [total, setTotal]           = useState(0);

    // Filtros
    const [filtros, setFiltros] = useState({
        conTelefono: false,
        conInfoBancaria: false,
        conInfoPersonal: false,
        conInfoProfesional: false,
        verificacionExitosa: false,
        verificacionPendiente: false,
        verificacionNegada: false,
        modoVendedor: false,
        aceptaMarketing: false,
        conReferido: false,
    });

    // Cargar lista al montar y cuando cambia página o filtros
    useEffect(() => {
        (async () => {
            try {
                setCargandoLista(true);
                const res = await axios.post("/listar-usuarios-admin", { params: { pagina, ...filtros } });
                setLista(res.data.usuarios || []);
                setPaginas(res.data.paginas || 1);
                setTotal(res.data.total || 0);
            } catch {
                setLista([]);
            } finally {
                setCargandoLista(false);
            }
        })();
    }, [pagina, filtros]);

    // Buscador con debounce
    useEffect(() => {
        if (!query.trim()) { setResultados([]); setBuscado(false); return; }
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => buscar(query.trim()), 400);
        return () => clearTimeout(timerRef.current);
    }, [query]);

    async function buscar(q) {
        try {
            setBuscando(true);
            const res = await axios.post("/buscar-usuarios", { params: { query: q } });
            setResultados(res?.data?.usuarios || []);
            setBuscado(true);
        } catch {
            setResultados([]);
            setBuscado(true);
        } finally {
            setBuscando(false);
        }
    }

    function irA(u) {
        navigate("/admin/usuario", { state: { params: { username: u.username } } });
    }

    function cambiarPagina(p) {
        setPagina(p);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function cambiarFiltros(nuevosFiltros) {
        setFiltros(nuevosFiltros);
        setPagina(1);
    }

    // Cuando hay búsqueda activa, mostrar resultados; si no, mostrar lista
    const mostrandoBusqueda = buscado || buscando || query.trim().length > 0;

    return (
        <div className="container-fluid">
            <div className="pt-3 pb-2 mb-4 border-bottom">
                <h1 className="h2 text-dark mb-1">Usuarios</h1>
                <p className="text-muted mb-0 small">
                    {total > 0 ? `${total.toLocaleString("es-CO")} usuarios registrados` : "Gestiona los usuarios de la plataforma."}
                </p>
            </div>

            {/* Buscador */}
            <div className="row justify-content-center mb-3">
                <div className="col-12 col-md-8 col-lg-6">
                    <div className="input-group shadow-sm">
                        <span className="input-group-text bg-white border-end-0 rounded-start-4">
                            {buscando
                                ? <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                : <i className="bi bi-search text-muted"></i>
                            }
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0 border-end-0 px-0"
                            placeholder="Buscar por nombre, @usuario o correo..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoFocus
                        />
                        {query && (
                            <button
                                type="button"
                                className="btn btn-white border border-start-0 rounded-end-4 text-muted"
                                onClick={() => { setQuery(""); setResultados([]); setBuscado(false); }}
                                tabIndex={-1}
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filtros */}
            {!mostrandoBusqueda && (
                <div className="row justify-content-center mb-4">
                    <div className="col-12 col-lg-10">
                        <FiltrosUsuarios filtros={filtros} onChange={cambiarFiltros} />
                    </div>
                </div>
            )}

            {/* Resultados de búsqueda */}
            {mostrandoBusqueda && (
                <div className="row justify-content-center mb-5">
                    <div className="col-12 col-lg-10">
                        {buscando && (
                            <div className="text-center py-4">
                                <span className="spinner-border spinner-border-sm text-primary" role="status" />
                            </div>
                        )}
                        {buscado && !buscando && (
                            resultados.length === 0 ? (
                                <div className="border rounded-4 p-5 bg-light text-center">
                                    <i className="bi bi-person-x fs-2 text-muted"></i>
                                    <p className="text-muted small mb-0 mt-2">No encontramos usuarios para <strong>"{query}"</strong>.</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-muted small mb-3">
                                        {resultados.length} resultado{resultados.length !== 1 ? "s" : ""} para <strong>"{query}"</strong>
                                        {resultados.length === 20 ? " — mostrando los primeros 20" : ""}
                                    </p>
                                    <ListaUsuarios usuarios={resultados} onVer={irA} />
                                </>
                            )
                        )}
                    </div>
                </div>
            )}

            {/* Lista paginada (siempre visible debajo del buscador) */}
            <div className="row justify-content-center">
                <div className="col-12 col-lg-10">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="fw-semibold mb-0 text-dark">Todos los usuarios</h6>
                        <span className="text-muted small">Página {pagina} de {paginas}</span>
                    </div>

                    {cargandoLista ? (
                        <div className="text-center py-5">
                            <span className="spinner-border text-primary" role="status" />
                        </div>
                    ) : (
                        <ListaUsuarios usuarios={lista} onVer={irA} />
                    )}

                    {/* Paginación */}
                    {paginas > 1 && !cargandoLista && (
                        <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                            <button
                                className="btn btn-outline-secondary btn-sm rounded-3"
                                onClick={() => cambiarPagina(pagina - 1)}
                                disabled={pagina === 1}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>

                            {Array.from({ length: paginas }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === paginas || Math.abs(p - pagina) <= 2)
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, i) =>
                                    p === "..." ? (
                                        <span key={`ellipsis-${i}`} className="text-muted px-1">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            className={`btn btn-sm rounded-3 px-3 ${p === pagina ? "btn-dark" : "btn-outline-secondary"}`}
                                            onClick={() => cambiarPagina(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                )
                            }

                            <button
                                className="btn btn-outline-secondary btn-sm rounded-3"
                                onClick={() => cambiarPagina(pagina + 1)}
                                disabled={pagina === paginas}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Usuarios;
